import { forwardRef } from "react";
import type { FactoryPayload, Factory } from "@mantine/core";

/**
 * More information about Mantine's component wrappers can be found here:
 * https://mantine.dev/guides/polymorphic/#wrapping-polymorphic-components
 * @param displayName Display name for the component, used for debugging purposes
 * @param render Render function for the component
 * @returns Wrapped component
 */
export const componentWrapper = <ElementType extends Factory<FactoryPayload>, Props>(
    displayName: string,
    render: React.ForwardRefRenderFunction<ElementType["ref"], React.PropsWithoutRef<Props>>
) => {
    // First we create the forwardRef component
    const ForwardedComponent = forwardRef<ElementType["ref"], Props>((props, ref) =>
        render(props, ref)
    ) as React.ForwardRefExoticComponent<React.PropsWithoutRef<Props> & React.RefAttributes<ElementType["ref"]>> &
        ElementType["staticComponents"];
    // Then we set the display name for debugging purposes
    if (displayName) {
        ForwardedComponent.displayName = displayName;
    }

    // Finally, we create the polymorphic component using Mantine's utility
    return ForwardedComponent;
};

/**
 * Generic version of componentWrapper for components that need to support generic types.
 * This is a simplified approach that creates a factory function for generic components.
 * @returns Function that creates wrapped components with specific generic types
 */
export const createGenericComponentWrapper = () => {
    return <ElementType extends Factory<FactoryPayload>, Props>(
        displayName: string,
        render: React.ForwardRefRenderFunction<ElementType["ref"], React.PropsWithoutRef<Props>>
    ) => {
        // First we create the forwardRef component
        const ForwardedComponent = forwardRef<ElementType["ref"], Props>((props, ref) =>
            render(props, ref)
        ) as React.ForwardRefExoticComponent<React.PropsWithoutRef<Props> & React.RefAttributes<ElementType["ref"]>> &
            ElementType["staticComponents"];

        // Then we set the display name for debugging purposes
        if (displayName) {
            ForwardedComponent.displayName = displayName;
        }

        return ForwardedComponent;
    };
};

export default componentWrapper;
